// Clickable status pill for action items. Cycles open → in_progress → resolved
// on click. Used both inside <ActionRow> (wrapped in <Link>) and standalone, so
// it intercepts Link navigation via stopPropagation + preventDefault.
//
// State changes go through mcp.addActionUpdate(update_type=status_change) so
// the transition lands in the updates timeline automatically.

import { useState } from "react";
import { Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/cn";
import { mcp, type ActionStatus } from "@/lib/mcp";

const NEXT_STATUS: Partial<Record<ActionStatus, ActionStatus>> = {
  open: "in_progress",
  in_progress: "resolved",
};

const STATUS_TONES: Record<string, string> = {
  open: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100",
  in_progress: "bg-blue-100 text-blue-900 dark:bg-blue-950 dark:text-blue-200",
  resolved: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  blocked: "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200",
  cancelled: "bg-muted text-muted-foreground",
  superseded: "bg-muted text-muted-foreground",
};

function statusLabel(s: ActionStatus | string): string {
  if (s === "in_progress") return "in progress";
  return String(s);
}

interface Props {
  code: string;
  status: ActionStatus;
  onAdvanced?: () => void;
  className?: string;
}

export function StatusPill({ code, status, onAdvanced, className }: Props) {
  const [busy, setBusy] = useState(false);
  const next = NEXT_STATUS[status];

  async function advance(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!next || busy) return;
    setBusy(true);
    try {
      await mcp.addActionUpdate({
        code,
        update_type: "status_change",
        from_value: status,
        to_value: next,
      });
      toast.success(`Moved to ${statusLabel(next)}`);
      onAdvanced?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const interactive = next !== undefined;

  return (
    <button
      type="button"
      onClick={advance}
      disabled={!interactive || busy}
      title={interactive ? `Click to move to ${statusLabel(next!)}` : statusLabel(status)}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase",
        STATUS_TONES[status] ?? STATUS_TONES.open,
        interactive && "hover:ring-2 hover:ring-primary/40 cursor-pointer",
        !interactive && "cursor-default",
        busy && "opacity-60",
        className,
      )}
    >
      {busy && <Loader2 className="h-3 w-3 animate-spin" />}
      {statusLabel(status)}
      {interactive && !busy && <ChevronRight className="h-3 w-3 opacity-60" />}
    </button>
  );
}
